import { useRef } from "react";
import { SHORTCUTS, shortcutLabel } from "../hooks/use-shortcuts";
import type { Command } from "./CommandPalette";
import { Icon, Logo } from "./Icon";
import { canUseRooms } from "./InsecureBanner";
import { type Menu, MenuBar } from "./MenuBar";

interface TopBarProps {
  /** What the command center shows (the open file). */
  title: string;
  menus: Menu[];
  commands: Command[];
  onOpenFile: (file: File) => void;
  onShare: () => void;
  /** Opens quick open (the command center, as in VS Code's title bar). */
  onQuickOpen: () => void;
  /** People & chat (the right-hand panel): whether it's open, and who's around. */
  peopleOpen: boolean;
  onTogglePeople: () => void;
  /** People in the room besides you. */
  peers: number;
  unread: number;
}

/**
 * VS Code's title bar: the app and its menu bar on the left, the command center (quick open) in
 * the middle, and sharing on the right.
 */
export function TopBar(props: TopBarProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  return (
    <header className="top-bar">
      <h1 className="brand">
        <Logo size={18} />
        <span className="brand-name">codescreen</span>
      </h1>
      <MenuBar menus={props.menus} commands={props.commands} />
      {/* File → Open File… clicks this. */}
      <input
        ref={fileInput}
        type="file"
        hidden
        data-testid="open-file-input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) props.onOpenFile(file);
          event.target.value = "";
        }}
      />
      <span className="top-bar-spacer" />
      <button
        type="button"
        className="command-center"
        aria-label={`Go to file (open: ${props.title})`}
        onClick={props.onQuickOpen}
        title={`Go to file (${shortcutLabel(SHORTCUTS.quickOpen)}), commands (${shortcutLabel(SHORTCUTS.commandPalette)})`}
      >
        <Icon name="search" size={14} />
        <span className="command-center-label">{props.title}</span>
      </button>
      <span className="top-bar-spacer" />
      <button
        type="button"
        id="toggle-side"
        className="people-toggle"
        aria-label="People & chat"
        aria-pressed={props.peopleOpen}
        title={`${props.peopleOpen ? "Hide" : "Show"} people & chat (${shortcutLabel(SHORTCUTS.togglePeople)})`}
        onClick={props.onTogglePeople}
        // Escape closes the panel even before focus has moved into it.
        onKeyDown={(e) => {
          if (e.key === "Escape" && props.peopleOpen) props.onTogglePeople();
        }}
      >
        <Icon name="users" size={15} />
        <span className="people-toggle-label">People & chat</span>
        {props.peers > 0 && (
          <span className="people-count" title={`${props.peers + 1} people in the room`}>
            {props.peers + 1}
          </span>
        )}
        {props.unread > 0 && !props.peopleOpen && (
          <span className="badge" data-testid="chat-unread">
            {props.unread}
          </span>
        )}
      </button>
      <button
        type="button"
        className="share-button"
        onClick={props.onShare}
        disabled={!canUseRooms()}
        title={
          canUseRooms()
            ? "Copy a link others can open to join"
            : "Sharing needs the page to be served over https"
        }
      >
        <Icon name="share" size={14} />
        <span className="share-label">Share</span>
      </button>
    </header>
  );
}
